"use client";

import { useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { useJobs } from "../../app/hooks/useJobs";
import { resumeSetupPath } from "../../app/lib/resume/dashboardHome";
import {
  normalizeJobsCompanySlug,
  resolveJobsCompanyName,
  resolveJobsDisplayName,
} from "../../app/lib/resume/jobsCompanyMap";

function logoSrc(company) {
  return `/images/logos/${String(company || "")
    .toLowerCase()
    .replaceAll(" ", "-")}.avif`;
}

function hasOfficialSalary(job) {
  const raw = job?.salary;
  return raw != null && String(raw).trim() !== "";
}

function formatSalaryValue(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return `$${n.toLocaleString()}`;
}

function pickJobsForBanner(jobs, limit) {
  const list = Array.isArray(jobs) ? jobs : [];
  const withOfficial = list.filter(hasOfficialSalary);
  const pool = withOfficial.length > 0 ? withOfficial : list;
  return pool.slice(0, limit);
}

/**
 * In-article CTA: live openings for the firm + register for ATS-tailored resume.
 * Prefer roles with official salary; CTA is product signup (no external Apply).
 */
export default function ArticleResumeBoostBanner({
  firmName = "",
  companySlug = "",
  articleSlug = "",
  utmSource,
  utmMedium,
  utmCampaign,
  highlights = [],
  note = "",
  jobLimit = 2,
}) {
  const slug = normalizeJobsCompanySlug(companySlug);
  const firm = resolveJobsDisplayName({ companySlug: slug, firmName });
  const companyForJobs = resolveJobsCompanyName({ companySlug: slug, firmName: firm });
  const limit = Math.min(Math.max(Number(jobLimit) || 2, 1), 4);
  // Fetch a wider page so we can prefer official-salary roles.
  const fetchSize = Math.min(Math.max(limit * 10, 20), 50);

  const { jobs, isLoading, isError } = useJobs({
    page: 1,
    perPage: fetchSize,
    company: companyForJobs,
    sortBy: "parsed_at",
    sortDir: "desc",
    enabled: Boolean(companyForJobs),
  });

  const shownJobs = useMemo(() => pickJobsForBanner(jobs, limit), [jobs, limit]);

  if (!slug && !firm) return null;

  const hasJobs = shownJobs.length > 0;

  const customBullets = Array.isArray(highlights) ? highlights.filter(Boolean).slice(0, 4) : [];
  const defaultBullets = hasJobs
    ? [
        "Upload your master resume once — we extract ATS keywords automatically.",
        `See your match score on open ${firm} roles, then Boost a tailored PDF before you apply.`,
      ]
    : [
        "Upload your master resume once — we extract ATS keywords automatically.",
        `When ${firm} roles reopen, you’ll already have match scores and can Boost a tailored PDF in one click.`,
      ];
  const bullets = customBullets.length > 0 ? customBullets : defaultBullets;

  const customNote = String(note || "").trim();
  const bodyNote =
    customNote ||
    (hasJobs
      ? `Create a free account to unlock the full job board, ATS match scores, and a ${firm}-tailored resume PDF — then apply on the company site with a stronger resume.`
      : `No live ${firm} openings in our board right now — create a free account anyway to upload your resume and be ready to Boost an ATS-ready PDF as soon as roles appear.`);

  const baseUtm = () => {
    const params = new URLSearchParams();
    params.set("utm_source", String(utmSource || "article").trim() || "article");
    params.set("utm_medium", String(utmMedium || "prep_resume_banner").trim() || "prep_resume_banner");
    params.set("utm_campaign", String(utmCampaign || articleSlug || "article").trim() || "article");
    params.set("utm_content", "resume_boost");
    return params;
  };

  const registerHref = (() => {
    const params = baseUtm();
    const afterAuthPath = `${resumeSetupPath()}&${params.toString()}`;
    return `/?register=1&next=${encodeURIComponent(afterAuthPath)}`;
  })();

  function tailorHrefForJob(jobId) {
    const params = baseUtm();
    params.set("utm_content", `tailor_job_${jobId}`);
    params.set("setup", "1");
    const afterAuthPath = `/dashboard/jobs/${encodeURIComponent(jobId)}/boost?${params.toString()}`;
    return `/?register=1&next=${encodeURIComponent(afterAuthPath)}`;
  }

  return (
    <div className="my-6 space-y-3 not-prose max-w-full">
      <aside
        className="rounded-lg border border-[#e4e2dd] bg-[#fdfbfa] p-4 md:p-5 text-left shadow-sm"
        aria-label={`Open roles at ${firm}`}
      >
        <div className="border-l-[3px] border-[#507619] pl-3 md:pl-4">
          <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-[#8a8883] mb-1">Open roles</p>
          <p className="text-sm font-semibold text-[#27251f] leading-snug">
            {hasJobs || isLoading ? (
              <>
                Live openings at <span className="text-[#507619]">{firm}</span>
              </>
            ) : (
              <>
                No active openings at <span className="text-[#507619]">{firm}</span> right now
              </>
            )}
          </p>

          {isLoading ? <p className="mt-3 text-xs text-[#6d6c66]">Loading roles…</p> : null}
          {isError ? <p className="mt-3 text-xs text-[#8a1f1f]">Could not load roles right now.</p> : null}

          {!isLoading && !isError && hasJobs ? (
            <ul className="mt-3 space-y-2">
              {shownJobs.map((job) => {
                const salaryLabel =
                  hasOfficialSalary(job) && job.salary_value != null
                    ? formatSalaryValue(job.salary_value)
                    : null;
                return (
                  <li
                    key={job.id}
                    className="flex flex-col gap-2 rounded-md border border-[#e4e2dd] bg-white px-3 py-2.5 sm:flex-row sm:items-center sm:gap-3"
                  >
                    <div className="flex min-w-0 flex-1 items-start gap-3">
                      <div className="relative mt-0.5 size-8 shrink-0 overflow-hidden rounded-sm bg-white shadow-sm">
                        <Image
                          src={logoSrc(companyForJobs || job.company || firm)}
                          alt=""
                          width={32}
                          height={32}
                          className="object-contain"
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-[#27251f] leading-snug truncate">
                          {job.position || "Role"}
                        </p>
                        <p className="mt-0.5 text-xs text-[#6d6c66] truncate">
                          {[job.location, job.program_type].filter(Boolean).join(" · ") || firm}
                        </p>
                      </div>
                      {salaryLabel ? (
                        <p className="shrink-0 self-center text-sm font-semibold tabular-nums text-[#27251f]">
                          {salaryLabel}
                        </p>
                      ) : null}
                    </div>
                    <Link
                      href={tailorHrefForJob(job.id)}
                      className="shrink-0 self-stretch sm:self-center rounded-md border border-[#507619] bg-[#507619] px-2.5 py-1.5 text-center text-xs font-medium !text-white hover:bg-[#456515] transition-colors"
                    >
                      Tailor resume
                    </Link>
                  </li>
                );
              })}
            </ul>
          ) : null}

          {!isLoading && !isError && !hasJobs ? (
            <p className="mt-3 text-xs text-[#6d6c66] leading-relaxed">
              We still track {firm} on the job board. Sign up free to get match scores across all firms and an ATS-ready PDF when a role opens.
            </p>
          ) : null}

          <p className="mt-3 text-[11px] text-[#8a8883] leading-relaxed text-right">
            After free signup: match score, Boost PDF, then apply on the company site.
          </p>
        </div>
      </aside>

      <aside
        className="rounded-lg border border-[#d6e4bf] bg-[#f4f8ee] p-4 md:p-5 text-left shadow-sm"
        aria-label="ATS resume for this firm"
      >
        <div className="border-l-[3px] border-[#507619] pl-3 md:pl-4">
          <p className="text-[0.65rem] font-semibold uppercase tracking-wide text-[#507619] mb-1">Free account</p>
          <p className="text-sm font-semibold text-[#27251f] leading-snug">
            Get an ATS-ready resume tailored for <span className="text-[#507619]">{firm}</span>
          </p>
          <p className="text-xs text-[#5f5e59] leading-relaxed mt-2">{bodyNote}</p>

          <ul className="mt-3 space-y-1.5 text-xs text-[#5f5e59]">
            {bullets.map((line, i) => (
              <li key={i} className="flex gap-2 items-start">
                <span className="mt-1.5 size-1 shrink-0 rounded-full bg-[#507619]" aria-hidden />
                <span className="leading-relaxed">{line}</span>
              </li>
            ))}
          </ul>

          <div className="mt-4">
            <Link
              href={registerHref}
              className="inline-flex w-full sm:w-auto items-center justify-center rounded-md border border-[#507619] bg-[#507619] px-4 py-2 text-sm font-medium !text-white hover:bg-[#456515] transition-colors"
            >
              Tailor my resume — free signup
            </Link>
          </div>
        </div>
      </aside>
    </div>
  );
}
